import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Wine, Calendar, MapPin, Check, AlertCircle } from "lucide-react";
import { api } from "../lib/api";

export default function CancelRsvp() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | found | invalid | cancelled
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get(`/rsvp/${token}`)
      .then((res) => {
        setInfo(res.data);
        setState("found");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const confirmCancel = async () => {
    setBusy(true);
    try {
      await api.post(`/rsvp/${token}/cancel`);
      setState("cancelled");
    } catch {
      setState("cancelled");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain relative min-h-screen bg-wine-bg text-cream flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 justify-center mb-10">
          <Wine className="h-6 w-6 text-gold" />
          <span className="font-display text-2xl tracking-tight">
            Macarthur <span className="italic text-terracotta">Wine</span> Society
          </span>
        </Link>

        <div className="rounded-2xl border border-white/10 bg-wine-surface p-8 md:p-10">
          {state === "loading" && (
            <p data-testid="cancel-loading" className="text-center text-smoke">Loading your reservation…</p>
          )}

          {state === "invalid" && (
            <div data-testid="cancel-invalid" className="text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-terracotta/15">
                <AlertCircle className="h-7 w-7 text-terracotta" />
              </div>
              <h1 className="font-display text-3xl">Link not found</h1>
              <p className="mt-3 text-smoke">This reservation link is invalid or your spot has already been cancelled.</p>
              <Link to="/" className="mt-6 inline-block rounded-full bg-gold px-6 py-3 text-sm font-semibold text-wine-bg hover:bg-cream transition-colors">Back to the site</Link>
            </div>
          )}

          {state === "found" && info && (
            <div data-testid="cancel-found">
              <h1 className="font-display text-3xl">Cancel your spot?</h1>
              <p className="mt-3 text-smoke">
                {info.name ? `${info.name}, we` : "We"}'ve got you down for:
              </p>
              <div className="mt-5 rounded-xl border border-white/10 bg-wine-bg p-5">
                <p className="font-display text-xl text-cream">{info.event_title}</p>
                {(info.event_date || info.event_time) && (
                  <p className="mt-2 flex items-center gap-2 text-sm text-smoke"><Calendar className="h-4 w-4 text-terracotta" /> {info.event_date} {info.event_time}</p>
                )}
                {info.event_location && (
                  <p className="mt-1 flex items-center gap-2 text-sm text-smoke"><MapPin className="h-4 w-4 text-terracotta" /> {info.event_location}</p>
                )}
              </div>
              <button
                data-testid="cancel-confirm-btn"
                onClick={confirmCancel}
                disabled={busy}
                className="mt-6 w-full rounded-full bg-terracotta py-3.5 text-sm font-semibold text-cream hover:bg-gold hover:text-wine-bg transition-colors disabled:opacity-60"
              >
                {busy ? "Cancelling…" : "Yes, cancel my spot"}
              </button>
              <Link to="/" className="mt-3 block text-center text-sm text-smoke hover:text-gold transition-colors">
                No, keep my reservation
              </Link>
            </div>
          )}

          {state === "cancelled" && (
            <div data-testid="cancel-done" className="text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gold/15">
                <Check className="h-7 w-7 text-gold" />
              </div>
              <h1 className="font-display text-3xl">Spot cancelled</h1>
              <p className="mt-3 text-smoke">No worries at all — your spot has been released. We hope to see you at a future gathering!</p>
              <p className="mt-2 text-sm text-smoke">Changed your mind? Just RSVP again from the site.</p>
              <Link to="/#events" className="mt-6 inline-block rounded-full bg-gold px-6 py-3 text-sm font-semibold text-wine-bg hover:bg-cream transition-colors">See gatherings</Link>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-smoke">
          Questions? Email <a href="mailto:cheers@macarthurwinesociety.com.au" className="text-gold hover:underline">cheers@macarthurwinesociety.com.au</a>
        </p>
      </div>
    </div>
  );
}
